import { FC, useEffect, useMemo, useState } from "react";

import { IAccommodationBlockParams } from "Bands/types";
import { FormBuilder } from "shared/components";
import { IAddress, IAPIError, IAPIState, IFormButtons } from "shared/types";

import {
  useBandRegionalSettings,
  useIsStartDateBeforeEndDate,
} from "Bands/utils";
import { getFormFields } from "./formData";
import { isValidTimestamp } from "Auth/utils/parseTimeField";

interface AccommodationBlockEntryFormProps {
  apiStatus?: IAPIState;
  error?: boolean | string;
  defaultValues?: any;
  buttons?: IFormButtons;
  apiError?: IAPIError | null;
  excludeUsers?: string[] | undefined;
  handleSubmit: (data: IAccommodationBlockParams) => void;
}

export const AccommodationBlockForm: FC<AccommodationBlockEntryFormProps> = ({
  apiStatus,
  error,
  buttons,
  handleSubmit,
  defaultValues,
  apiError,
  children,
}) => {
  const { dateFormat, timeFormat } = useBandRegionalSettings();
  const [address, setAddress] = useState<IAddress | null>(
    defaultValues?.address || null
  );

  const [tempAddress, setTempAddress] = useState<IAddress | null>(
    defaultValues?.address || null
  );

  const [forceUpdateValues, setForceUpdateValues] =
    useState<IAccommodationBlockParams | undefined>();

  const startBeforeEndValues = useMemo(
    () => ({
      startDate: defaultValues?.checkInDate,
      startTime: defaultValues?.checkInTime,
      endDate: defaultValues?.checkOutDate,
      endTime: defaultValues?.checkOutTime,
    }),
    [defaultValues]
  );

  const {
    start,
    end,
    isStartBeforeEnd,
    handleStartDateChange,
    handleEndDateChange,
  } = useIsStartDateBeforeEndDate({
    defaultValues: startBeforeEndValues,
    includeTime: false,
  });

  const handleBlurAddress = (address: IAddress | null) => {
    setTempAddress(address);
  };

  const submitAddress = (address: IAddress | null) => {
    setAddress(address);
  };

  const preventSubmit = useMemo(() => !address, [address]);

  const onSubmit = (partialAccommodation: IAccommodationBlockParams) => {
    if (address) {
      if ((partialAccommodation?.amenities as unknown) === "")
        partialAccommodation.amenities = []; // Cast empty string to empty array.

      const accommodation: IAccommodationBlockParams = {
        ...partialAccommodation,
        checkInTime: isValidTimestamp(start?.time) ? start?.time : "",
        checkOutTime: isValidTimestamp(end?.time) ? end?.time : "",
        address,
      };

      handleSubmit(accommodation);
    }
  };

  const fields = useMemo(
    () =>
      getFormFields({
        isStartBeforeEnd,
        handleStartDateChange,
        handleEndDateChange,
        dateFormat,
        timeFormat,
        submitAddress,
        defaultValues: {
          ...defaultValues,
          address: (address || tempAddress) as IAddress | undefined,
        },
        handleBlurAddress,
      }),
    [
      dateFormat,
      defaultValues,
      handleEndDateChange,
      handleStartDateChange,
      isStartBeforeEnd,
      timeFormat,
      address,
      tempAddress,
    ]
  );

  // if start date changes, update end date to the future
  useEffect(() => {
    if (start?.date && end?.date && !defaultValues.address) {
      const startDateCompare = new Date(start?.date);
      const endDateCompare = new Date(end?.date);

      if (startDateCompare > endDateCompare) {
        const forcedEndDate = new Date(start.date);
        forcedEndDate.setDate(forcedEndDate.getDate() + 1);

        setForceUpdateValues({
          checkOutDate: forcedEndDate.toISOString().split("T")[0],
        });

        handleEndDateChange({
          ...end,
          date: forcedEndDate.toISOString().split("T")[0],
        });
      }
    }
  }, [start?.date, end?.date, handleEndDateChange, end, defaultValues.address]);

  return (
    <>
      <FormBuilder
        isSubmitting={apiStatus === IAPIState.pending}
        apiErrorMessage={error}
        fields={fields}
        onSubmit={onSubmit}
        buttons={buttons}
        defaultValues={defaultValues}
        apiError={apiError}
        preventSubmit={preventSubmit}
        showSaveChanges={true}
        forceUpdateValues={forceUpdateValues}
      />
      {children}
    </>
  );
};
